import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { createNotification, createOrganizationNotification } from './notifications';

function normalizeStep(step = {}) {
  return {
    id: step.id || '',
    title: step.title || '',
    description: step.description || '',
    complete: Boolean(step.complete),
    ctaLabel: step.ctaLabel || '',
    ctaPath: step.ctaPath || '',
  };
}

function getMembershipCount(members = []) {
  return (members || []).filter((member) => member?.status !== 'inactive').length;
}

function getInstructorCount(members = []) {
  return (members || []).filter((member) =>
    ['instructor', 'organization_admin', 'organization_owner'].includes(member?.orgRole || member?.role)
      && member?.status !== 'inactive'
  ).length;
}

export function getInvitePostAcceptPath(invite = {}, { userData = {} } = {}) {
  const role = invite?.role || userData?.orgRole || userData?.role || 'student';

  if (['organization_owner', 'organization_admin'].includes(role)) {
    return '/organization';
  }

  if (role === 'instructor') {
    return '/dashboard';
  }

  return '/dashboard';
}

export function getOrganizationOnboardingState({
  organization = {},
  members = [],
  invites = [],
  templates = [],
  commercialState = {},
} = {}) {
  const membershipCount = getMembershipCount(members);
  const instructorCount = getInstructorCount(members);
  const pendingInviteCount = (invites || []).filter((invite) => invite?.status === 'pending').length;
  const templateCount = (templates || []).length;
  const hasCommercialAccess = Boolean(
    commercialState?.currentPlan ||
    commercialState?.isTrialing ||
    (commercialState?.includedPacks || []).length ||
    (commercialState?.addOnPacks || []).length
  );

  const steps = [
    normalizeStep({
      id: 'confirm_organization',
      title: 'Confirm organization profile',
      description: 'Make sure the organization has its core profile and can be identified clearly by members.',
      complete: Boolean(organization?.name || organization?.organizationId || organization?.id),
      ctaLabel: 'Review workspace',
      ctaPath: '/organization',
    }),
    normalizeStep({
      id: 'invite_team',
      title: 'Invite your first team member',
      description: 'Invite another instructor or admin so the organization can start collaborating.',
      complete: instructorCount > 1 || pendingInviteCount > 0 || membershipCount > 1,
      ctaLabel: 'Invite members',
      ctaPath: '/organization',
    }),
    normalizeStep({
      id: 'add_content',
      title: 'Add your first teaching content',
      description: 'Create a drill or content item so your organization has something reusable in its library.',
      complete: templateCount > 0,
      ctaLabel: 'Open drill library',
      ctaPath: '/content/drills/new',
    }),
    normalizeStep({
      id: 'review_access',
      title: 'Review plan and premium access',
      description: 'Understand what is currently included and what premium content can be requested next.',
      complete: hasCommercialAccess,
      ctaLabel: 'Review commercial state',
      ctaPath: '/organization',
    }),
  ];

  const completedStepCount = steps.filter((step) => step.complete).length;
  const recommendedStep = steps.find((step) => !step.complete) || null;

  return {
    steps,
    completedStepCount,
    totalStepCount: steps.length,
    completionPercent: steps.length ? Math.round((completedStepCount / steps.length) * 100) : 0,
    isComplete: steps.every((step) => step.complete),
    recommendedStep,
    pendingInviteCount,
    instructorCount,
    membershipCount,
    templateCount,
  };
}

export function getTrialDaysRemaining(commercialState = {}) {
  const trialEndAt = commercialState?.subscription?.trialEndAt || commercialState?.subscription?.currentPeriodEndAt;
  if (!trialEndAt) return null;

  const endTime = trialEndAt?.seconds
    ? trialEndAt.seconds * 1000
    : new Date(trialEndAt).getTime();

  if (!Number.isFinite(endTime)) return null;
  return Math.max(0, Math.ceil((endTime - Date.now()) / (24 * 60 * 60 * 1000)));
}

export function getOrganizationCommercialExperience({
  organization = {},
  commercialState = {},
  entitlementSummary = {},
} = {}) {
  const pendingRequestType = organization?.commercialRequestType || '';
  const pendingRequestStatus = organization?.commercialRequestStatus || '';

  return {
    currentPlanName: commercialState?.currentPlan?.name || 'No plan',
    subscriptionStatus: commercialState?.subscription?.status || 'inactive',
    currentAccessLabel: commercialState?.isTrialing
      ? 'Trial'
      : commercialState?.subscription
        ? 'Subscription'
        : (commercialState?.addOnPacks || []).length
          ? 'Manual or add-on access'
          : 'Limited access',
    trialDaysRemaining: getTrialDaysRemaining(commercialState),
    lockedPremiumPackCount: (entitlementSummary?.unavailablePremiumPacks || []).length,
    accessiblePremiumPackCount: (entitlementSummary?.accessiblePacks || []).length,
    hasPendingRequest: pendingRequestStatus === 'pending',
    pendingRequestType,
    pendingRequestStatus,
    pendingRequestRequestedByName: organization?.commercialRequestRequestedByName || '',
    pendingRequestContentPackId: organization?.commercialRequestContentPackId || '',
  };
}

function buildRequestCopy({ requestType = 'plan_upgrade', contentPackName = '' } = {}) {
  switch (requestType) {
    case 'trial':
      return {
        title: 'Trial requested',
        message: 'Your organization requested a trial so it can explore more of the platform.',
      };
    case 'content_pack_access':
      return {
        title: 'Content pack access requested',
        message: contentPackName
          ? `Access to ${contentPackName} was requested for this organization.`
          : 'A premium content pack was requested for this organization.',
      };
    case 'plan_upgrade':
    default:
      return {
        title: 'Plan upgrade requested',
        message: 'Your organization requested a plan upgrade or commercial review.',
      };
  }
}

export async function submitOrganizationUpgradeRequest(
  db,
  {
    organizationId = '',
    organizationName = '',
    requestType = 'plan_upgrade',
    contentPackId = '',
    contentPackName = '',
    note = '',
    requestedByUserId = '',
    requestedByName = '',
  } = {}
) {
  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  await updateDoc(doc(db, 'organizations', organizationId), {
    commercialRequestStatus: 'pending',
    commercialRequestType: requestType,
    commercialRequestContentPackId: contentPackId || '',
    commercialRequestNote: note || '',
    commercialRequestRequestedByUserId: requestedByUserId || '',
    commercialRequestRequestedByName: requestedByName || '',
    commercialRequestUpdatedAt: serverTimestamp(),
  });

  const copy = buildRequestCopy({ requestType, contentPackName });

  try {
    if (requestedByUserId) {
      await createNotification(db, {
        userId: requestedByUserId,
        organizationId,
        audience: 'organization_staff', // Pass 85.1B.3 — operator who requested the trial/upgrade (staff)
        type: requestType === 'trial' ? 'organization_trial_requested' : 'organization_upgrade_requested',
        title: copy.title,
        message: copy.message,
        link: '/organization',
        actorUserId: requestedByUserId,
        actorName: requestedByName,
        relatedEntityType: requestType === 'content_pack_access' ? 'content_pack' : 'organization',
        relatedEntityId: contentPackId || organizationId,
        metadata: {
          requestType,
          contentPackId,
          note,
          organizationName,
        },
      });
    }

    await createOrganizationNotification(db, {
      organizationId,
      roles: ['organization_owner', 'organization_admin'],
      excludeUserIds: requestedByUserId ? [requestedByUserId] : [],
      type: requestType === 'trial' ? 'organization_trial_requested' : 'organization_upgrade_requested',
      title: copy.title,
      message: copy.message,
      link: '/organization',
      actorUserId: requestedByUserId,
      actorName: requestedByName,
      relatedEntityType: requestType === 'content_pack_access' ? 'content_pack' : 'organization',
      relatedEntityId: contentPackId || organizationId,
      metadata: {
        requestType,
        contentPackId,
        note,
        organizationName,
      },
    });
  } catch (error) {
    console.error('Failed to notify organization upgrade request:', error);
  }

  return {
    organizationId,
    requestType,
    contentPackId,
    status: 'pending',
  };
}
